import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  KeyRound,
  Layout,
  Mail,
  Palette,
  Rocket,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import type { WorkspaceSetupState, WorkspaceRole, WorkspacesResult } from '@blogforge/shared';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';

// ─────────────────────────────────────────────────────────────────
// Workspace onboarding wizard (/workspace/setup)
//
// A new workspace is created bare — a name, an owner and an empty site
// settings row — so its owner is walked through this once. Steps write
// through the endpoints that already own their data (site settings, invites,
// billing) rather than through an onboarding store, which is why leaving
// mid-way and coming back shows what was already saved and why the server
// only has to remember `completedAt`/`skippedAt`.
//
// Steps:
//   1. Your account   — replace the admin-issued password (optional)
//   2. Workspace      — workspace + site name, description, base URL
//   3. Theme          — pick the active theme
//   4. Team           — invite teammates (optional, repeatable)
//   5. Plan           — where the workspace stands, or start the trial
//
// Every step except the finishing action is skippable: "Skip setup" ends the
// wizard immediately and the recap in Settings is where the rest can wait.
// ─────────────────────────────────────────────────────────────────

type StepId = 'account' | 'workspace' | 'theme' | 'team' | 'plan';

interface Step {
  id: StepId;
  title: string;
  blurb: string;
  icon: typeof KeyRound;
}

const STEPS: Step[] = [
  {
    id: 'account',
    title: 'Your account',
    blurb: 'Replace the password you were given, so only you know it.',
    icon: KeyRound,
  },
  {
    id: 'workspace',
    title: 'Workspace & site',
    blurb: 'Name the blog and tell it where it will be published.',
    icon: Layout,
  },
  {
    id: 'theme',
    title: 'Theme',
    blurb: 'Pick the look your posts and emails render with.',
    icon: Palette,
  },
  {
    id: 'team',
    title: 'Your team',
    blurb: 'Invite the people who will write and review with you.',
    icon: UserPlus,
  },
  {
    id: 'plan',
    title: 'Plan',
    blurb: 'Where this workspace stands, and what it unlocks.',
    icon: Rocket,
  },
];

export function WorkspaceSetupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const user = useAuthStore((s) => s.user);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const setup = useQuery({
    queryKey: ['workspace-setup', activeWorkspaceId],
    queryFn: () => api.workspaces.setup(activeWorkspaceId!),
    enabled: Boolean(activeWorkspaceId),
    staleTime: 0,
    retry: false,
  });

  const state = setup.data;

  // Only owners/admins can finish setup — a plain member who lands here
  // (shared link, stale tab) is sent back to the app instead of being shown
  // a wizard whose writes would 403.
  const role: WorkspaceRole | undefined = state?.workspace.role;
  const canFinish = role === 'owner' || role === 'admin';

  const finish = useMutation({
    mutationFn: (action: 'complete' | 'skip') =>
      api.workspaces.setupAction(activeWorkspaceId!, action),
    onSuccess: async (result) => {
      // AppShell redirects into this wizard from the cached `['workspaces']`
      // list, and it reads that cache the instant we navigate. Patching it
      // synchronously is what keeps the guard from seeing a stale
      // `needsSetup: true` and bouncing the user straight back here; the
      // invalidate afterwards reconciles with the server (and refetches even
      // with no mounted observer, which a plain invalidate does not).
      qc.setQueryData<WorkspacesResult>(['workspaces'], (old) =>
        old
          ? {
              workspaces: old.workspaces.map((w) =>
                w.id === activeWorkspaceId ? { ...w, needsSetup: result.needsSetup } : w,
              ),
            }
          : old,
      );
      await qc.invalidateQueries({ queryKey: ['workspaces'], refetchType: 'all' });
      navigate('/posts', { replace: true });
    },
    onError: (err) =>
      setError(err instanceof Error && err.message ? err.message : 'Could not save that.'),
  });

  useEffect(() => {
    if (state && !canFinish) navigate('/posts', { replace: true });
  }, [state, canFinish, navigate]);

  // No active workspace yet (fresh login before the switcher has picked one).
  useEffect(() => {
    if (!activeWorkspaceId) navigate('/posts', { replace: true });
  }, [activeWorkspaceId, navigate]);

  const step = STEPS[stepIndex]!;
  const isLast = stepIndex === STEPS.length - 1;

  const advance = () => {
    setError(null);
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  if (!activeWorkspaceId) return null;

  return (
    <div className="bg-bg text-ink min-h-screen">
      <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-8 px-6 py-10 lg:flex-row lg:py-16">
        {/* ── Step rail ─────────────────────────────────────────── */}
        <aside className="lg:w-[280px] lg:shrink-0">
          <p className="eyebrow">
            <Sparkles size={12} /> Workspace setup
          </p>
          <h1 className="display mt-4 text-[1.75rem]">
            {state?.workspace.name ?? 'Your workspace'}
          </h1>
          <p className="text-ink-soft text-body-sm mt-2">
            A few minutes now saves a lot of tidying later. Every step can be skipped, and nothing
            here is permanent.
          </p>

          <ol className="mt-8 space-y-1">
            {STEPS.map((s, index) => {
              const done = index < stepIndex;
              const active = index === stepIndex;
              const Icon = s.icon;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setStepIndex(index)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold transition-colors ${
                      active
                        ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
                        : 'text-ink-soft hover:bg-ink/5'
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] ${
                        done
                          ? 'bg-accent text-accent-ink'
                          : active
                            ? 'bg-accent/15 text-accent'
                            : 'bg-ink/8 text-muted'
                      }`}
                    >
                      {done ? <Check size={12} strokeWidth={3} /> : <Icon size={12} />}
                    </span>
                    {s.title}
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* ── Step body ─────────────────────────────────────────── */}
        <main className="min-w-0 flex-1">
          <div className="glass-card p-6 sm:p-8">
            <p className="text-muted font-mono text-[11px] tracking-[0.04em] uppercase">
              Step {stepIndex + 1} of {STEPS.length}
            </p>
            <h2 className="text-heading-sm mt-2 font-semibold">{step.title}</h2>
            <p className="text-ink-soft text-body-sm mt-1">{step.blurb}</p>

            {error && (
              <div
                role="alert"
                className="border-danger/40 text-danger text-body-sm rounded-btn mt-5 border px-3.5 py-3"
              >
                {error}
              </div>
            )}

            <div className="mt-6">
              {setup.isLoading && <p className="text-muted text-[13px]">Loading your workspace…</p>}
              {setup.isError && (
                <p className="text-danger text-[13px]">
                  Could not load this workspace&apos;s setup state. Reload to try again.
                </p>
              )}
              {state && step.id === 'account' && <AccountStep onDone={advance} />}
              {state && step.id === 'workspace' && (
                <WorkspaceStep
                  state={state}
                  workspaceId={activeWorkspaceId}
                  onError={setError}
                  onDone={advance}
                />
              )}
              {state && step.id === 'theme' && (
                <ThemeStep
                  state={state}
                  workspaceId={activeWorkspaceId}
                  onError={setError}
                  onDone={advance}
                />
              )}
              {state && step.id === 'team' && (
                <TeamStep
                  state={state}
                  workspaceId={activeWorkspaceId}
                  onError={setError}
                  onDone={advance}
                />
              )}
              {state && step.id === 'plan' && <PlanStep state={state} onDone={advance} />}
            </div>

            {/* ── Footer controls ───────────────────────────────── */}
            <div className="border-line mt-8 flex flex-wrap items-center justify-between gap-3 border-t pt-5">
              <button
                type="button"
                onClick={() => finish.mutate('skip')}
                disabled={finish.isPending || !canFinish}
                className="text-muted hover:text-ink text-[12.5px] font-medium underline-offset-2 hover:underline"
              >
                Skip setup for now
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                  disabled={stepIndex === 0}
                  className="btn-ghost px-3 py-2 text-[12.5px] disabled:opacity-40"
                >
                  <ArrowLeft size={13} /> Back
                </button>

                {isLast ? (
                  <button
                    type="button"
                    onClick={() => finish.mutate('complete')}
                    disabled={finish.isPending || !canFinish}
                    className="btn-primary px-4 py-2 text-[13px]"
                  >
                    {finish.isPending ? (
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <CheckCircle2 size={14} />
                    )}
                    Finish setup
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={advance}
                    className="btn-primary px-4 py-2 text-[13px]"
                  >
                    Continue <ArrowRight size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <p className="text-muted mt-4 px-1 text-[11.5px]">
            Signed in as {user?.email ?? 'you'} — you own this workspace, so only you and your
            admins can change these settings.
          </p>
        </main>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 1 — account
// ─────────────────────────────────────────────────────────────────

function AccountStep({ onDone }: { onDone: () => void }) {
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    if (newPassword.length < 8) return setError('The new password must be at least 8 characters.');
    if (newPassword !== confirm) return setError('The new passwords do not match.');
    if (newPassword === currentPassword) {
      return setError('The new password must be different from the current one.');
    }

    setBusy(true);
    try {
      await api.auth.changePassword({ currentPassword, newPassword }, refreshToken);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border-success/30 bg-success/6 rounded-btn border px-4 py-3">
        <p className="text-body-sm font-semibold">Password changed.</p>
        <p className="text-ink-soft mt-0.5 text-[12.5px]">
          Other sessions were signed out. Yours stays active.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(['current', 'new', 'confirm'] as const).map((field) => {
        const meta = {
          current: {
            label: 'Current password',
            value: currentPassword,
            set: setCurrentPassword,
            autoComplete: 'current-password',
          },
          new: {
            label: 'New password',
            value: newPassword,
            set: setNewPassword,
            autoComplete: 'new-password',
          },
          confirm: {
            label: 'Confirm new password',
            value: confirm,
            set: setConfirm,
            autoComplete: 'new-password',
          },
        }[field];

        return (
          <div key={field}>
            <label htmlFor={`pw-${field}`} className="field-label">
              {meta.label}
            </label>
            <input
              id={`pw-${field}`}
              type="password"
              autoComplete={meta.autoComplete}
              value={meta.value}
              onChange={(e) => meta.set(e.target.value)}
              className="field-input"
            />
          </div>
        );
      })}

      {error && <p className="text-danger text-[12.5px]">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          {busy ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <KeyRound size={13} />
          )}
          Change password
        </button>
        <button type="button" onClick={onDone} className="btn-ghost px-3 py-2 text-[12.5px]">
          Already changed it — continue
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 2 — workspace & site
// ─────────────────────────────────────────────────────────────────

function WorkspaceStep({
  state,
  workspaceId,
  onDone,
  onError,
}: {
  state: WorkspaceSetupState;
  workspaceId: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const qc = useQueryClient();
  const [workspaceName, setWorkspaceName] = useState(state.workspace.name);
  const [siteName, setSiteName] = useState(state.site?.siteName ?? state.workspace.name);
  const [siteDescription, setSiteDescription] = useState(state.site?.siteDescription ?? '');
  const [baseUrl, setBaseUrl] = useState(state.site?.baseUrl ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    onError(null);
    if (!workspaceName.trim()) return onError('Give the workspace a name.');
    if (!siteName.trim()) return onError('Give the site a name.');

    setBusy(true);
    try {
      // Two owners of this data: the workspace row carries the name shown in
      // the switcher, site settings carry what gets published. Saving both
      // here keeps them in step during onboarding.
      if (workspaceName.trim() !== state.workspace.name) {
        await api.workspaces.rename(workspaceId, workspaceName.trim());
      }
      await api.settings.updateSite({
        siteName: siteName.trim(),
        siteDescription: siteDescription.trim() || null,
        ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['workspaces'] }),
        qc.invalidateQueries({ queryKey: ['workspace-setup', workspaceId] }),
        qc.invalidateQueries({ queryKey: ['site-settings'] }),
      ]);
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not save these details.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="ws-name" className="field-label">
          Workspace name
        </label>
        <input
          id="ws-name"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          maxLength={80}
          className="field-input"
        />
        <p className="text-muted mt-1 text-[10.5px]">
          Shown in the workspace switcher. Also what separates one blog from another.
        </p>
      </div>

      <div>
        <label htmlFor="site-name" className="field-label">
          Site name
        </label>
        <input
          id="site-name"
          value={siteName}
          onChange={(e) => setSiteName(e.target.value)}
          className="field-input"
        />
      </div>

      <div>
        <label htmlFor="site-description" className="field-label">
          Site description
        </label>
        <textarea
          id="site-description"
          value={siteDescription}
          onChange={(e) => setSiteDescription(e.target.value)}
          rows={2}
          placeholder="What this blog is about — used as the default meta description."
          className="field-input resize-none"
        />
      </div>

      <div>
        <label htmlFor="base-url" className="field-label">
          Base URL
        </label>
        <input
          id="base-url"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://blog.example.com"
          className="field-input"
        />
        <p className="text-muted mt-1 text-[10.5px]">
          Where this site is published. Canonical URLs, sitemap entries and email links are built
          from it.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          {busy ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : null}
          Save & continue
        </button>
        <button type="button" onClick={onDone} className="btn-ghost px-3 py-2 text-[12.5px]">
          Keep as is
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 3 — theme
// ─────────────────────────────────────────────────────────────────

function ThemeStep({
  state,
  workspaceId,
  onDone,
  onError,
}: {
  state: WorkspaceSetupState;
  workspaceId: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(state.site?.activeThemeId ?? state.themes[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    onError(null);
    if (!selected) return onError('No theme is installed on this deployment.');
    if (selected === state.site?.activeThemeId) return onDone();

    setBusy(true);
    try {
      await api.settings.updateSite({ activeThemeId: selected });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['workspace-setup', workspaceId] }),
        qc.invalidateQueries({ queryKey: ['site-settings'] }),
      ]);
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not switch theme.');
    } finally {
      setBusy(false);
    }
  };

  if (state.themes.length === 0) {
    return (
      <p className="text-ink-soft text-[13px]">
        No themes are installed on this deployment yet. An administrator can add one from Settings
        &rarr; Themes.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {state.themes.map((theme) => {
          const active = theme.id === selected;
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => setSelected(theme.id)}
              className={`rounded-card border p-4 text-left transition-colors ${
                active ? 'border-accent bg-accent/6' : 'border-line hover:border-line-strong'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13.5px] font-semibold">{theme.name}</span>
                {active && <Check size={14} className="text-accent shrink-0" strokeWidth={3} />}
              </div>
              {theme.description && (
                <p className="text-ink-soft mt-1 text-[12px]">{theme.description}</p>
              )}
              <p className="text-muted mt-2 font-mono text-[10.5px] tracking-[0.04em] uppercase">
                {theme.isBundled ? 'Bundled' : 'Installed'}
              </p>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          {busy ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Palette size={13} />
          )}
          Use this theme
        </button>
        <button type="button" onClick={onDone} className="btn-ghost px-3 py-2 text-[12.5px]">
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 4 — team
// ─────────────────────────────────────────────────────────────────

function TeamStep({
  state,
  workspaceId,
  onDone,
  onError,
}: {
  state: WorkspaceSetupState;
  workspaceId: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const invite = async () => {
    onError(null);
    if (!email.trim()) return onError('Enter an email address to invite.');

    setBusy(true);
    try {
      const res = await api.workspaces.invite(workspaceId, { email: email.trim(), role });
      setInviteUrl(res.acceptUrl);
      setEmail('');
      setCopied(false);
      await qc.invalidateQueries({ queryKey: ['workspace-setup', workspaceId] });
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not create that invite.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
    } catch {
      onError('Could not copy automatically — select the link and copy it manually.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_130px]">
        <div>
          <label htmlFor="invite-email" className="field-label">
            Email address
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void invite();
            }}
            placeholder="teammate@example.com"
            className="field-input"
          />
        </div>
        <div>
          <label htmlFor="invite-role" className="field-label">
            Role
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
            className="field-input"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      <button
        type="button"
        onClick={() => void invite()}
        disabled={busy}
        className="btn-primary px-4 py-2 text-[13px]"
      >
        {busy ? (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <UserPlus size={13} />
        )}
        Create invite link
      </button>

      {inviteUrl && (
        <div className="border-line bg-bg-soft/60 rounded-btn border p-3">
          <p className="text-[12.5px] font-semibold">Invite link ready — send it to them</p>
          <p className="text-ink-soft mt-1 text-[11.5px]">
            Email delivery is not configured on this deployment yet, so the link is yours to pass
            on. It expires in 7 days.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="bg-card border-line min-w-0 flex-1 truncate rounded-lg border px-2 py-1.5 font-mono text-[11px]">
              {inviteUrl}
            </code>
            <button
              type="button"
              onClick={() => void copy()}
              className="btn-ghost shrink-0 px-2.5 py-1.5 text-[11.5px]"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <p className="text-muted text-[11.5px]">
        {state.team.memberCount} {state.team.memberCount === 1 ? 'member' : 'members'}
        {state.team.pendingInviteCount > 0
          ? ` · ${state.team.pendingInviteCount} invite${state.team.pendingInviteCount === 1 ? '' : 's'} pending`
          : ''}
        . You can invite more later from Workspace &rarr; Members.
      </p>

      <button type="button" onClick={onDone} className="btn-ghost px-3 py-2 text-[12.5px]">
        {state.team.memberCount > 1 ? 'Done inviting' : 'Just me for now'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 5 — plan
// ─────────────────────────────────────────────────────────────────

function PlanStep({ state, onDone }: { state: WorkspaceSetupState; onDone: () => void }) {
  const navigate = useNavigate();
  const current = state.subscription;

  const label = useMemo(() => {
    if (!current) return 'Free';
    const name = current.plan.charAt(0).toUpperCase() + current.plan.slice(1);
    return current.status === 'trialing' ? `${name} (trial)` : name;
  }, [current]);

  return (
    <div className="space-y-4">
      <div className="border-line rounded-card border p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-muted font-mono text-[10.5px] tracking-[0.04em] uppercase">
              Current plan
            </p>
            <p className="mt-1 text-[15px] font-semibold">{label}</p>
          </div>
          {current?.currentPeriodEnd && (
            <p className="text-ink-soft text-right text-[11.5px]">
              Renews/ends
              <br />
              {new Date(current.currentPeriodEnd).toLocaleDateString()}
            </p>
          )}
        </div>
        <p className="text-ink-soft mt-3 text-[12.5px]">
          {current
            ? 'Your plan is active. Unlimited posts, emails and components are unlocked.'
            : 'Free covers 3 posts and 3 emails. Start the 14-day Individual trial to unlock unlimited posts, emails and the component library.'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/billing/plans')}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          <Rocket size={13} />
          {current ? 'Manage plan' : 'See plans & trial'}
        </button>
        <button type="button" onClick={onDone} className="btn-ghost px-3 py-2 text-[12.5px]">
          Decide later
        </button>
      </div>

      <p className="text-muted text-[11.5px]">
        <Mail size={11} className="mr-1 inline" />
        An administrator can also place this workspace on a plan directly.
      </p>
    </div>
  );
}
